/**
 * LogRocket Monitoring Provider
 *
 * Provides session replay, error tracking, and user analytics through LogRocket
 * Implements MonitoringProvider interface for consistent API
 */

import LogRocket from 'logrocket';
import { env, isProduction } from '@/shared/lib/env';
import type {
  MonitoringProvider,
  UserTraits,
  ErrorContext,
  EventProperties,
  BreadcrumbData,
} from '../types';

/**
 * Type alias for LogRocket primitive values
 */
type LogRocketPrimitive = string | number | boolean;

/**
 * LogRocket provider implementation
 */
class LogRocketProvider implements MonitoringProvider {
  readonly name = 'LogRocket';
  private initialized = false;

  /**
   * Initialize LogRocket with configuration
   */
  initialize(): void {
    // ── P2P chat privacy guard ────────────────────────────────────────────
    // Never start LogRocket while the user is on /chat. The session-replay
    // would otherwise capture decrypted messages from the DOM. The hard
    // reload in ChatRouteGuard ensures stale instances are not reused.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/chat')
    ) {
      return;
    }
    // ──────────────────────────────────────────────────────────────────────

    if (this.initialized) {
      console.warn(`${this.name}: Already initialized`);
      return;
    }

    const appId = env.VITE_LOGROCKET_APP_ID;
    if (!appId) {
      console.warn(`${this.name}: App ID not configured in environment`);
      return;
    }

    // Check if any monitoring feature is enabled
    const sessionReplayEnabled = env.VITE_ENABLE_SESSION_REPLAY;
    const errorTrackingEnabled = env.VITE_ENABLE_ERROR_TRACKING;

    if (!sessionReplayEnabled && !errorTrackingEnabled) {
      if (!isProduction) {
        console.log(
          `${this.name}: Both session replay and error tracking disabled`
        );
      }
      return;
    }

    try {
      LogRocket.init(appId, {
        // Respect user privacy - only capture production traffic
        shouldCaptureIP: isProduction,

        // Console aggregation - only when session replay is enabled
        console: {
          shouldAggregateConsoleErrors: errorTrackingEnabled,
          isEnabled: {
            log: sessionReplayEnabled && isProduction,
            info: sessionReplayEnabled && isProduction,
            debug: false, // Never capture debug logs
            warn: errorTrackingEnabled,
            error: errorTrackingEnabled,
          },
        },

        // Network request/response sanitization
        network: {
          // Only capture network activity if session replay is enabled
          isEnabled: sessionReplayEnabled,
          requestSanitizer: (request) =>
            this.sanitizeRequest(request) as typeof request,
          responseSanitizer: (response) =>
            this.sanitizeResponse(response) as typeof response,
        },

        // DOM recording options - only when session replay is enabled
        dom: {
          isEnabled: sessionReplayEnabled,
          inputSanitizer: true, // Automatically sanitize input fields
          textSanitizer: false, // Keep text content for debugging
        },

        // Performance settings
        mergeIframes: false,
      });

      this.initialized = true;

      // Mark a global flag so `ChatRouteGuard` can detect a stale LogRocket
      // instance after an in-app navigation to /chat and force a reload.
      if (typeof window !== 'undefined') {
        (
          window as Window & { __LOGROCKET_INITIALIZED__?: boolean }
        ).__LOGROCKET_INITIALIZED__ = true;
      }

      // Log what features are enabled
      const features: string[] = [];
      if (sessionReplayEnabled) features.push('Session Replay');
      if (errorTrackingEnabled) features.push('Error Tracking');

      if (!isProduction) {
        console.log(
          `${this.name} initialized successfully with: ${features.join(', ')}`
        );
      }
    } catch (error) {
      console.error(`${this.name} initialization failed:`, error);
    }
  }

  /**
   * Sanitize sensitive data from network requests
   */
  private sanitizeRequest(request: unknown): typeof request {
    const req = request as {
      headers?: Record<string, string>;
      body?: string | Record<string, unknown>;
    };

    if (!req) return req;

    try {
      // Sanitize sensitive headers
      if (req.headers) {
        const sanitizedHeaders = { ...req.headers };

        // Remove authentication tokens
        const authKeys = ['Authorization', 'authorization', 'X-API-Key'];
        for (const key of authKeys) {
          if (sanitizedHeaders[key]) {
            sanitizedHeaders[key] = '[REDACTED]';
          }
        }

        req.headers = sanitizedHeaders;
      }

      // Sanitize request body passwords
      if (req.body) {
        const body = this.parseBody(req.body);
        if (body) {
          const sanitizedBody = this.sanitizePasswords(body);
          req.body =
            typeof req.body === 'string'
              ? JSON.stringify(sanitizedBody)
              : sanitizedBody;
        }
      }
    } catch {
      // If sanitization fails, return original request
    }

    return req;
  }

  /**
   * Sanitize sensitive data from network responses
   */
  private sanitizeResponse(response: unknown): typeof response {
    const res = response as {
      body?: string | Record<string, unknown>;
    };

    if (!res?.body) return res;

    try {
      const body = this.parseBody(res.body);
      if (body) {
        const sanitizedBody = this.sanitizeTokens(body);
        res.body =
          typeof res.body === 'string'
            ? JSON.stringify(sanitizedBody)
            : sanitizedBody;
      }
    } catch {
      // If sanitization fails, return original response
    }

    return res;
  }

  /**
   * Parse body to object
   */
  private parseBody(
    body: string | Record<string, unknown>
  ): Record<string, unknown> | null {
    if (typeof body === 'string') {
      try {
        return JSON.parse(body) as Record<string, unknown>;
      } catch {
        return null;
      }
    }
    return body && typeof body === 'object' ? body : null;
  }

  /**
   * Sanitize password fields from object
   */
  private sanitizePasswords(
    obj: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized = { ...obj };
    const passwordKeys = [
      'password',
      'confirmPassword',
      'oldPassword',
      'newPassword',
    ];

    for (const key of passwordKeys) {
      if (key in sanitized) {
        sanitized[key] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  /**
   * Sanitize token fields from object
   */
  private sanitizeTokens(
    obj: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized = { ...obj };
    const tokenKeys = ['token', 'refreshToken', 'accessToken'];

    for (const key of tokenKeys) {
      if (key in sanitized && typeof sanitized[key] === 'string') {
        sanitized[key] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  /**
   * Identify user in LogRocket
   */
  identifyUser(userId: string, traits?: UserTraits): void {
    // P2P chat privacy guard — never identify users on /chat.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/chat')
    ) {
      return;
    }

    if (!this.initialized) {
      return;
    }

    try {
      // Build traits object with only defined values
      const sanitizedTraits: Record<string, LogRocketPrimitive> = {};

      if (traits?.name) sanitizedTraits['name'] = traits.name;
      if (traits?.email) sanitizedTraits['email'] = traits.email;
      if (traits?.role) sanitizedTraits['role'] = traits.role;
      if (traits?.organizationId)
        sanitizedTraits['organizationId'] = traits.organizationId;

      // Add remaining traits (filter out undefined)
      if (traits) {
        for (const [key, value] of Object.entries(traits)) {
          if (
            value !== undefined &&
            !['name', 'email', 'role', 'organizationId'].includes(key) &&
            (typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value === 'boolean')
          ) {
            sanitizedTraits[key] = value;
          }
        }
      }

      LogRocket.identify(userId, sanitizedTraits);
      if (!isProduction) {
        console.log(`${this.name}: User identified - ${userId}`);
      }
    } catch (error) {
      console.error(`${this.name}: Failed to identify user:`, error);
    }
  }

  /**
   * Capture error in LogRocket
   */
  captureError(error: Error, context?: ErrorContext): void {
    if (!this.initialized) {
      return;
    }

    try {
      // Build exception context
      const exceptionContext: {
        tags?: Record<string, string>;
        extra?: Record<string, LogRocketPrimitive>;
      } = {};

      if (context?.tags) {
        exceptionContext.tags = { ...context.tags };
      }

      if (context?.extra) {
        // Filter extra to only include primitive types
        const sanitizedExtra: Record<string, LogRocketPrimitive> = {};
        for (const [key, value] of Object.entries(context.extra)) {
          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
          ) {
            sanitizedExtra[key] = value;
          } else if (value !== null && value !== undefined) {
            sanitizedExtra[key] = JSON.stringify(value);
          }
        }
        exceptionContext.extra = sanitizedExtra;
      }

      LogRocket.captureException(error, exceptionContext);

      // Also log to console for development visibility
      if (!isProduction) {
        console.error(`${this.name}: Captured error:`, error, context);
      }
    } catch (captureError) {
      console.error(`${this.name}: Failed to capture error:`, captureError);
    }
  }

  /**
   * Track custom event in LogRocket
   */
  trackEvent(event: string, properties?: EventProperties): void {
    if (!this.initialized) {
      return;
    }

    try {
      LogRocket.track(event, properties ? { ...properties } : undefined);

      // Log in development for debugging
      if (!isProduction) {
        console.log(`${this.name}: Event tracked - ${event}`, properties);
      }
    } catch (error) {
      console.error(`${this.name}: Failed to track event:`, error);
    }
  }

  /**
   * Add breadcrumb for debugging context
   */
  addBreadcrumb(message: string, data?: BreadcrumbData): void {
    if (!this.initialized) {
      return;
    }

    try {
      // LogRocket captures console.log as breadcrumbs automatically
      LogRocket.log(`[Breadcrumb] ${message}`, data ? { ...data } : undefined);
    } catch (error) {
      console.error(`${this.name}: Failed to add breadcrumb:`, error);
    }
  }

  /**
   * Set custom data for current session
   */
  setCustomData(key: string, value: unknown): void {
    if (!this.initialized) {
      return;
    }

    try {
      // Use LogRocket.log to add custom session data
      LogRocket.log(`[CustomData] ${key}:`, value);
    } catch (error) {
      console.error(`${this.name}: Failed to set custom data:`, error);
    }
  }

  /**
   * Clear user identification on logout
   */
  clearUser(): void {
    if (!this.initialized) {
      return;
    }

    try {
      // LogRocket doesn't have explicit clearUser, but we can track logout
      this.trackEvent('User Logged Out');
      if (!isProduction) {
        console.log(`${this.name}: User cleared`);
      }
    } catch (error) {
      console.error(`${this.name}: Failed to clear user:`, error);
    }
  }

  /**
   * Get current session URL (LogRocket-specific)
   */
  getSessionURL(): string | null {
    if (!this.initialized) {
      return null;
    }

    try {
      return LogRocket.sessionURL;
    } catch (error) {
      console.error(`${this.name}: Failed to get session URL:`, error);
      return null;
    }
  }
}

/**
 * Singleton instance
 */
export const logRocketProvider = new LogRocketProvider();
